import type {
  ComparisonBasis,
  IPeriodFilter,
  MonthKey,
  PeriodOverride,
} from '../types/period';
import { MonthKeyHelper } from './MonthKeyHelper';

export type PeriodPresetId =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'ytd'
  | 'ttm';

export const PERIOD_PRESET_LABELS: Record<PeriodPresetId, string> = {
  this_month: 'This month',
  last_month: 'Last month',
  this_quarter: 'This quarter',
  ytd: 'Year to date',
  ttm: 'Trailing 12 months',
};

export const PERIOD_PRESET_IDS = Object.keys(
  PERIOD_PRESET_LABELS
) as PeriodPresetId[];

export const COMPARISON_MENU_LABELS: Record<ComparisonBasis, string> = {
  prior_period: 'Prior period',
  prior_year: 'Prior year',
  trailing_3_avg: 'Trailing 3-month average',
};

export const COMPARISON_MENU_IDS = Object.keys(
  COMPARISON_MENU_LABELS
) as ComparisonBasis[];

/**
 * Builds the partial `PeriodOverride` values the card menu writes to `IReportItem.override`.
 *
 * Presets are relative to the report anchor, so a card set to "Last month" moves when the
 * report period moves. "This ..." presets leave `anchor` unset and inherit it through
 * `PeriodHelper.mergeFilter`; "Last month" stores an anchor that `rebase` shifts whenever
 * the report anchor changes. Only a custom range (`rangeStart`) pins absolute months.
 */
export class InlineOverrideHelper {
  static presetPeriod(
    preset: PeriodPresetId,
    reportAnchor: MonthKey
  ): Pick<PeriodOverride, 'grain' | 'anchor' | 'rangeStart'> {
    if (preset === 'last_month') {
      return {
        grain: 'month',
        anchor: MonthKeyHelper.add(reportAnchor, -1),
        rangeStart: null,
      };
    }
    const grain =
      preset === 'this_month'
        ? 'month'
        : preset === 'this_quarter'
          ? 'quarter'
          : preset;
    return { grain, rangeStart: null };
  }

  /** Shifts anchored (non-range) overrides by the months the report anchor moved. */
  static rebase(
    override: PeriodOverride | null | undefined,
    fromAnchor: MonthKey,
    toAnchor: MonthKey
  ): PeriodOverride | null | undefined {
    if (!override?.anchor || override.rangeStart || fromAnchor === toAnchor) {
      return override;
    }
    return {
      ...override,
      anchor: MonthKeyHelper.add(
        override.anchor,
        MonthKeyHelper.diff(toAnchor, fromAnchor)
      ),
    };
  }

  static hasPeriodOverride(override: PeriodOverride | null | undefined) {
    return Boolean(
      override &&
        (override.grain !== undefined ||
          override.anchor !== undefined ||
          (override.rangeStart !== undefined && override.rangeStart !== null))
    );
  }

  static isCustomRange(override: PeriodOverride | null | undefined) {
    return Boolean(override?.rangeStart);
  }

  static activePreset(
    override: PeriodOverride | null | undefined,
    filter: IPeriodFilter
  ): PeriodPresetId | null {
    if (!override || InlineOverrideHelper.isCustomRange(override)) return null;
    return (
      PERIOD_PRESET_IDS.find((preset) => {
        const expected = InlineOverrideHelper.presetPeriod(
          preset,
          filter.anchor
        );
        return (
          override.grain === expected.grain &&
          (override.anchor ?? filter.anchor) ===
            (expected.anchor ?? filter.anchor)
        );
      }) ?? null
    );
  }

  static withPreset(
    override: PeriodOverride | null | undefined,
    preset: PeriodPresetId,
    filter: IPeriodFilter
  ): PeriodOverride {
    return {
      ...InlineOverrideHelper.comparisonOnly(override),
      ...InlineOverrideHelper.presetPeriod(preset, filter.anchor),
    };
  }

  static withRange(
    override: PeriodOverride | null | undefined,
    rangeStart: MonthKey,
    anchor: MonthKey
  ): PeriodOverride {
    return {
      ...InlineOverrideHelper.comparisonOnly(override),
      grain: 'month',
      rangeStart,
      anchor,
    };
  }

  /** Drops the period part; returns `null` when nothing else is overridden. */
  static clearPeriod(
    override: PeriodOverride | null | undefined
  ): PeriodOverride | null {
    return InlineOverrideHelper.orNull(
      InlineOverrideHelper.comparisonOnly(override)
    );
  }

  static withComparison(
    override: PeriodOverride | null | undefined,
    comparison: ComparisonBasis
  ): PeriodOverride {
    return { ...InlineOverrideHelper.periodOnly(override), comparison };
  }

  /** Drops the comparison part; returns `null` when nothing else is overridden. */
  static clearComparison(
    override: PeriodOverride | null | undefined
  ): PeriodOverride | null {
    return InlineOverrideHelper.orNull(
      InlineOverrideHelper.periodOnly(override)
    );
  }

  private static comparisonOnly(
    override: PeriodOverride | null | undefined
  ): PeriodOverride {
    return override?.comparison ? { comparison: override.comparison } : {};
  }

  private static periodOnly(
    override: PeriodOverride | null | undefined
  ): PeriodOverride {
    if (!override) return {};
    return Object.fromEntries(
      Object.entries(override).filter(([key]) => key !== 'comparison')
    );
  }

  private static orNull(override: PeriodOverride): PeriodOverride | null {
    return Object.keys(override).length ? override : null;
  }
}
